import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { villaAPI, calendarAPI } from '../config/api';
import GuestReviewWall from '../components/GuestReviewWall';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { stripDiscount } from '../utils/discountText';
import VillaCardCarousel from '../components/VillaCardCarousel';
import { withBhk } from '../utils/villaName';
import { useSettings } from '../context/SettingsContext';
import {
  FiArrowRight, FiArrowLeft, FiUsers, FiCalendar, FiShield, FiStar,
  FiMapPin, FiCheck, FiPercent, FiHome, FiDroplet,
  FiPhone as _FiPhone, FiSearch, FiChevronDown, FiWifi, FiAward, FiHeart, FiX,
  FiCoffee, FiZap
} from 'react-icons/fi';
import { Calendar } from '../components/ui/calendar';

const API = process.env.REACT_APP_BACKEND_URL;

// Deterministic sort order for the home-page card grid + rates table.
// Matches the host's requested sequence: 3→4→5→7→8→9→12 BHK.
const VILLA_SEQUENCE = [
  '3bhk-garden-escape-lonavala',
  '4bhk-garden-retreat-lonavala',
  '5bhk-garden-delight-lonavala',
  '7bhk-harmony-villa-lonavala',
  '8bhk-grandeur-villa-lonavala',
  '9bhk-paradise-villa-lonavala',
  '12bhk-celebration-villa-lonavala',
];
const sortBySequence = (list) => [...list].sort(
  (a, b) => VILLA_SEQUENCE.indexOf(a.id) - VILLA_SEQUENCE.indexOf(b.id)
);

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/bqiogpix_RG%20Banner.png";

const LIFESTYLE_IMAGES = {
  pool: "https://images.unsplash.com/photo-1767950470198-c9cd97f8ed87?w=600&q=80",
  poolNight: "https://images.unsplash.com/photo-1775667447327-16d590d63180?w=600&q=80",
  dining: "https://images.unsplash.com/photo-1763479142464-2fc1249206df?w=600&q=80",
  celebration: "https://images.pexels.com/photos/27195983/pexels-photo-27195983.jpeg?auto=compress&cs=tinysrgb&w=600",
  bedroom: "https://images.unsplash.com/photo-1760067537740-faa11f7bdf1e?w=600&q=80",
  lights: "https://images.unsplash.com/photo-1755011211696-bd881bca0dca?w=600&q=80",
};

const COLLECTIONS = [
  { title: "Family Getaways", desc: "Bonding time with kids in private pool villas with gardens", img: "https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/qdo4n45i_Family%20Gateways.PNG", link: "/villas" },
  { title: "Birthday Celebrations", desc: "Throw the most memorable birthday party at our villas", img: "https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/hpynta9n_Birthday%20Celebrations.PNG", link: "/villas" },
  { title: "Weddings & Pre-Weddings", desc: "Grand venues for your most special celebrations", img: "https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/pcy0zdcy_Weddings%20and%20Pre%20Weddig.PNG", link: "/villas" },
  { title: "Film & TV Shoots", desc: "Scenic backdrops for productions and photoshoots", img: "https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/6u625f08_Film%20%26%20TV%20Shoots.PNG", link: "/shooting" },
  { title: "Corporate Offsites", desc: "Outdoor strategy sessions on lush open-lawn setups", img: "https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/23bdce699579ffe3_image.png", link: "/corporate" },
  { title: "Team Workshops", desc: "Evening brainstorming under a magic Lonavala sky", img: "https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/e1f39e5c1a21a1be_image.png", link: "/corporate" },
  { title: "Leadership Retreats", desc: "Intimate indoor strategy rooms for executive sessions", img: "https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/b2b23d6c44fae0de_Gemini_Generated_Image_6opmb96opmb96opm.png", link: "/corporate" },
  { title: "Business Conferences", desc: "Bright double-height living rooms for board-room feel", img: "https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/587e6b8bf197260f_Gemini_Generated_Image_xkg996xkg996xkg9.png", link: "/corporate" },
];

const FAQ_ITEMS = [
  { q: "What makes Royal Garden Villas the best luxury villa in Lonavala for groups?", a: "Royal Garden Villas offers exclusive, fully serviced premium villas with private swimming pools and lawns inside a gated estate with 24/7 security and housekeeping (10 AM – 6 PM). With seven options from 3BHK to 12BHK for 6 to 35 guests, it is ideal for family weekend getaways, friends trips, celebrations and premium corporate retreats in Lonavala, near Mumbai and Pune." },
  { q: "Which is the best villa in Lonavala with a private swimming pool for my group size?", a: "At Royal Garden Villas: 5 to 6 people — 3BHK Garden Escape (from ₹10,999/night); 8 to 10 people — 4BHK Garden Retreat (from ₹13,999); 10 to 15 people — 5BHK Garden Delight (from ₹16,999); 15 to 18 people — 7BHK villa; 16 to 20 people — 8BHK villa; 20 to 22 people — 9BHK villa; 25 to 35 people — 12BHK Celebration Villa (from ₹49,999). Every villa has its own private swimming pool." },
  { q: "Is there a villa in Lonavala for 15 to 20 people with a swimming pool?", a: "Yes. The 7BHK Harmony Villa (14–18 guests) and the 8BHK Grandeur Villa (16–20 guests) each combine two adjoining private villas in our gated compound — two private swimming pools, two lawns and two kitchens, ideal for large families and friend groups of 15 to 20 people." },
  { q: "Is there a villa in Lonavala for 20 to 25 people with a swimming pool?", a: "Yes. The 9BHK Paradise Villa takes 18–22 guests with two private pools, hot tub and BBQ area, and for 24 or 25 people the 12BHK Celebration Villa (24 included, up to 35) is the right choice." },
  { q: "Which villa in Lonavala is best for 25 to 35 people or a 30–35 people corporate offsite trip?", a: "The 12BHK Celebration Villa — three contiguous private villas (3BHK + 4BHK + 5BHK) in one gated compound with 3 private pools, 3 lawns, complimentary 50 Mbps Wi-Fi and large TV casting. It hosts 24 guests in the base price and up to 35, making it the go-to big villa in Lonavala for large groups and corporate offsites." },
  { q: "Which is the best place to stay in Lonavala with family for a staycation?", a: "For a family staycation in Lonavala, a private pool villa beats a hotel: your own pool (with baby pool or shallow zone), private lawn, AC bedrooms with attached washrooms and a serving kitchen for kids' food — inside a gated estate with 24/7 security. Our 3BHK, 4BHK and 5BHK villas are the family favourites." },
  { q: "Can I book a bungalow in Lonavala for the weekend?", a: "Yes. Each 3BHK, 4BHK and 5BHK at Royal Garden Villas is a standalone private bungalow with its own swimming pool and lawn, bookable for one night or the whole weekend. Check-in is 1:00 PM and check-out is 10:00 AM; the 2nd night onwards is 15% off." },
  { q: "Is there a villa in Lonavala for a birthday party, bachelor party or private party?", a: "Yes. Our private party villas are exclusive to your group — private pool, lawn, sound system (3BHK) or hot tub and BBQ area (5BHK), with outdoor music till 10 PM as per local norms. BYOB is allowed for private consumption and you can bring your own decorator, caterer." },
  { q: "Do you have a villa with food and cook in Lonavala?", a: "We don't run an in-house restaurant, so you get full food freedom: order from Swiggy, Zomato or local restaurants, hire any caterer, or book our optional Dedicated Cooking Space (per day) and bring your own cook / maharaj. Maid service (₹2,000/day) and BBQ service (₹3,000/meal) are available as add-ons." },
  { q: "Is there a budget luxury villa in Lonavala with a private pool?", a: "Yes. On weekdays our 3BHK starts at ₹10,999 and the 4BHK at ₹13,999 per night for the whole villa with a private pool — roughly ₹1,600–₹1,850 per person. Stay a 2nd night and every night from the 2nd onwards is 15% off." },
  { q: "Which villa in Lonavala is best for a corporate group of 20?", a: "The 12BHK Celebration Villa at Royal Garden Villas is perfect for corporate groups of up to 35 people. It combines all 3 villas with 12 bedrooms, 12 washrooms, 3 private pools, 3 kitchens, and expansive lawns for team activities. Located on Tata Dam Road, Lonavala, just 5 minutes from Mapro Gardens." },
  { q: "Are there private pool villas near Lucky Wax Museum in Lonavala?", a: "Yes! Royal Garden Villas is located inside the lane of Lucky Wax Museum on Tata Dam Road, Lonavala. We offer 3BHK, 4BHK, 5BHK and 12BHK private pool villas with split AC, gardens, and complimentary WiFi. Approximately 5 minutes from Mapro Gardens." },
  { q: "What is the check-in and check-out time?", a: "Check-in is at 1:00 PM and check-out is at 10:00 AM. Early check-in (12 PM) and late check-out (2 PM) can be arranged at Rs.2,000 each, subject to availability. Late checkout beyond complimentary half hour is charged at Rs.3,000 per hour." },
  { q: "How far is Royal Garden Villas from Mumbai and Pune?", a: "Royal Garden Villas is located in Lonavala, Maharashtra — approximately 2 hours from Mumbai (83 km via Mumbai-Pune Expressway) and 1.5 hours from Pune (64 km). Nearest railway station: Lonavala (5 km)." },
  { q: "What amenities are included in the 5BHK Garden Delight villa?", a: "The 5BHK Garden Delight includes 5 bedrooms with 5 washrooms, split AC in all rooms and living area, private swimming pool, hot tub, baby pool, play area, manicured garden, complimentary high-speed WiFi, fully equipped kitchen, parking for 2 cars, housekeeping (10 AM – 6 PM) and 24/7 security. Optional add-ons: Maid Service (Rs.2,000/day), BBQ (Rs.3,000/meal), and Bonfire (Rs.2,000/session)." },
  { q: "What about food and catering at Royal Garden Villas?", a: "Villa rent is not inclusive of food. Royal Garden Villas offers direct ordering facility — guests can order and pay directly to Caterers/Cloud Kitchens who take orders on KG basis with free home delivery. Swiggy & Zomato also works. Kitchen equipped with Crockery, Induction and Microwave. Contact details of Caterers & Restaurants available at Villa." },
  { q: "Do you offer the night-onwards discount for all villas?", a: "Yes! Book 2 or more nights at any villa — 3BHK, 4BHK, 5BHK or 12BHK — and every night from the 2nd onwards gets a flat {PCT}% off. The 1st night is at the actual nightly rate. Discount is auto-applied at checkout." },
  { q: "Can I book Royal Garden Villas for a film or TV shoot?", a: "Yes! Royal Garden Villas is a premium shooting location for films, TV shows, music videos, brand campaigns, and pre-wedding shoots. Rates: Rs.30,000/day (3BHK) to Rs.1,00,000/day (12BHK). Features diverse interiors, natural backdrops, private pools, and golden hour sunset views." },
  { q: "What is the Celebration Villa?", a: "The 12BHK Celebration Villa combines all 3 individual villas into one grand property with 12 bedrooms, 12 washrooms, 3 private pools, 3 kitchens, and expansive garden spaces. Accommodates up to 35 guests. Perfect for weddings, corporate offsites, and large celebrations." },
  { q: "What is the cancellation policy?", a: "Free cancellation is available up to 1 week before check-in. Cancellations within 7 days are non-refundable. Security deposit is fully refundable on the next day of checkout post inspection. For 50% advance bookings, balance must be paid by 1 PM one day before check-in or booking is auto-cancelled with no refund." },
];

const NEARBY = [
  {
    name: "Lucky Wax Museum",
    dist: "0.7 km",
    desc: "Wax statues of Bollywood, sports & global celebrities",
    img: "/attractions/lucky-wax-museum.png",
    url: "https://luckyscelebritywaxmuseum.com/",
  },
  {
    name: "Bouncing Brothers Trampoline Park",
    dist: "0.7 km",
    desc: "Indoor trampoline arena, foam pits & dodgeball for all ages",
    img: "/attractions/bouncing-brothers.png",
    url: "https://bouncingbrothertrampolineparklonvala.com/",
  },
  {
    name: "One Fun Park",
    dist: "1.2 km",
    desc: "Family amusement park near Darshan Valley, Varsoli",
    img: "/attractions/one-fun-park.png",
    url: "https://maps.app.goo.gl/x5UjytzFj1nvNjPw9?g_st=ac",
  },
  {
    name: "Trick Vision Lonavala",
    dist: "1.5 km",
    desc: "3D illusion museum on the Old Mumbai-Pune Highway",
    img: "/attractions/trick-vision.png",
    url: "https://maps.app.goo.gl/BCJzKMkMZw1X9zR96?g_st=aw",
  },
  {
    name: "Mapro Garden",
    dist: "1.9 km",
    desc: "Iconic strawberry farm, cafe, jam-tasting & shopping",
    img: "/attractions/mapro-garden.png",
    url: "https://www.mapro.com/pages/explore-lonavala-garden",
  },
  {
    name: "Wet'n Joy",
    dist: "8.6 km",
    desc: "Largest water park & amusement park in Maharashtra",
    img: "https://www.lonavala.wetnjoy.in/wp-content/uploads/2025/04/Wetnjoy-WP-and-AP-rides.jpg",
    url: "https://www.lonavala.wetnjoy.in/",
  },
  {
    name: "Bhushi Dam",
    dist: "9.3 km",
    desc: "Iconic monsoon waterfall steps over the Indrayani river",
    img: "https://commons.wikimedia.org/wiki/Special:FilePath/Bhushi_Dam_-_Lonavala.jpg?width=800",
    url: "https://www.google.com/search?q=Bhushi+Dam",
  },
  {
    name: "Tiger Point",
    dist: "15 km",
    desc: "Panoramic valley views & spectacular Sahyadri sunsets",
    img: "https://commons.wikimedia.org/wiki/Special:FilePath/Tiger_Point_Lonavala.jpg?width=800",
    url: "https://www.google.com/search?q=tiger+point+lonavala",
  },
];


const Home = () => {
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedVilla, setSelectedVilla] = useState('');
  const [guests, setGuests] = useState(6);
  const [homeCheckIn, setHomeCheckIn] = useState(null);
  const [homeCheckOut, setHomeCheckOut] = useState(null);
  const [homeCalendarOpen, setHomeCalendarOpen] = useState(null);
  const [homeBlockedDates, setHomeBlockedDates] = useState(new Set());
  // Feature flags for the new combo villas + cooking amenity. Default true so
  // the home page renders optimistically while the fetch is in flight.
  const [combosEnabled, setCombosEnabled] = useState(true);
  const [cookingEnabled, setCookingEnabled] = useState(true);
  const [cookingPricing, setCookingPricing] = useState({});
  const navigate = useNavigate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();

  const toDateStr = (date) => {
    const yy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  const formatDisplay = (d) => {
    if (!d) return 'Select date';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    villaAPI.getAll()
      .then(res => setVillas(sortBySequence(res.data)))
      .catch(err => console.error('Error fetching villas:', err))
      .finally(() => setLoading(false));

    // Fetch feature flags (combo villas + cooking amenity) + cooking pricing.
    // Non-blocking: if these fail, we fall back to combos ON / cooking ON.
    axios.get(`${API}/api/features/combos-and-cooking`)
      .then(r => {
        setCombosEnabled(!!r.data?.combo_villas_enabled);
        setCookingEnabled(!!r.data?.cooking_amenity_enabled);
      })
      .catch(() => { /* keep defaults */ });
    axios.get(`${API}/api/features/cooking-amenity`)
      .then(r => setCookingPricing(r.data?.pricing || {}))
      .catch(() => { /* non-critical */ });
  }, []);

  // Fetch blocked dates when villa is selected on home page
  useEffect(() => {
    if (!selectedVilla) { setHomeBlockedDates(new Set()); return; }
    calendarAPI.getAvailability(selectedVilla)
      .then(res => {
        const dateStrings = new Set();
        for (const b of (res.data.blocked_dates || [])) {
          const [sy, sm, sd] = b.start_date.split('-').map(Number);
          const [ey, em, ed] = b.end_date.split('-').map(Number);
          let cur = new Date(sy, sm - 1, sd);
          const end = new Date(ey, em - 1, ed);
          while (cur < end) {
            dateStrings.add(toDateStr(cur));
            cur.setDate(cur.getDate() + 1);
          }
        }
        setHomeBlockedDates(dateStrings);
      })
      .catch(() => setHomeBlockedDates(new Set()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVilla]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedVilla) params.set('villa', selectedVilla);
    if (homeCheckIn) params.set('checkin', toDateStr(homeCheckIn));
    if (homeCheckOut) params.set('checkout', toDateStr(homeCheckOut));
    if (guests) params.set('adults', guests.toString());
    navigate(`/booking?${params.toString()}`);
  };

  // JSON-LD: WebPage + Speakable schema for voice assistants & AI answer engines
  // (Perplexity, Google SGE, ChatGPT, Gemini). Brand-wide AggregateRating +
  // Review schema is auto-injected by <SEO/> from reviews.js and lives at
  // @id = royalgardenvillas.in/#brand-product so it stays consistent across
  // every page on the site.
  const reviewsSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Royal Garden Villas — Private Pool Villas in Lonavala on Rent',
      description: 'Luxury 3BHK to 12BHK private swimming pool villas in Lonavala on rent — family holidays, corporate offsites up to 35 persons, birthdays. Complimentary 50 Mbps Wi-Fi, optional Dedicated Cooking Space (bring your own chef / maharaj).',
      url: 'https://royalgardenvillas.in/',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['[data-speakable]'],
      },
    },
  ];

  return (
    <div data-testid="home-page">
      <SEO
        title="Royal Garden Villas | Luxury Private Pool Villas in Lonavala — 3BHK to 12BHK for 6–35 Guests"
        description="Book an unforgettable stay at Royal Garden Villas — premium luxury villas in Lonavala with private swimming pools, lawns, housekeeping and 24/7 security in a gated estate. 3BHK to 12BHK for 6–35 guests, perfect for families, friends and corporate groups. From ₹10,999/night."
        path="/"
        jsonLd={reviewsSchema}
      />
      {/* ===================== HERO SECTION ===================== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-start">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Royal Garden Villas — Luxury Private Pool Villas in Lonavala, Maharashtra"
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B2432]/95 via-[#1B2432]/80 to-[#1B2432]/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-36 sm:pt-40 md:pt-44 lg:pt-48 pb-16 w-full">
          <div className="max-w-2xl mb-20">
            <h1
              className="text-4xl sm:text-5xl lg:text-7xl leading-[1.15] mb-8 fade-in-up"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}
            >
              <span className="text-white block mb-3">Luxury Private Pool Villas in Lonavala</span>
              <span className="block text-[#F57C20] italic">For Families &amp; Celebrations</span>
            </h1>
            <p data-speakable="true" className="text-white/80 text-base md:text-lg leading-relaxed mb-10 max-w-lg fade-in-up fade-in-up-delay-1" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
              Royal Garden Villas is a gated luxury villa estate in Lonavala&apos;s Western Ghats, offering 3BHK to 12BHK private pool villas for family weekends, corporate offsites, birthdays &amp; pre-wedding celebrations — 2 hours from Mumbai, 90 minutes from Pune.
            </p>
          </div>

          {/* ===== PULSING COOKING BANNER — floating directly above search card ===== */}
          {cookingEnabled && (
            <div className="flex justify-center md:justify-start mb-3 md:mb-4">
              <a
                href="#cooking-amenity-card"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector('[data-testid="cooking-amenity-card"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                data-testid="hero-cooking-banner"
                className="group relative inline-flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-[#1E3A8A] via-[#1E40AF] to-[#2563EB] border border-white/25 shadow-xl shadow-[#1E3A8A]/40 backdrop-blur-md cursor-pointer transition-transform duration-300 hover:scale-[1.03] cooking-pulse-blue overflow-hidden"
              >
                {/* Pulsing ring overlay */}
                <span className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-[#60A5FA] animate-ping opacity-40" aria-hidden="true" />

                {/* Icon chip */}
                <div className="relative w-9 h-9 rounded-full bg-white/20 border border-white/40 flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <FiCoffee className="w-4 h-4 text-white" />
                </div>

                {/* Copy */}
                <div className="relative flex items-center gap-2 min-w-0">
                  <span className="text-[9px] md:text-[10px] font-black tracking-[0.18em] uppercase bg-white text-[#1E3A8A] px-2 py-0.5 rounded-full animate-pulse flex-shrink-0">
                    ✨ New
                  </span>
                  <div className="flex flex-col leading-tight">
                    <p className="text-white text-[12px] md:text-[13px] font-bold whitespace-nowrap" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Dedicated Cooking Space
                      <span className="hidden sm:inline text-white/70 font-normal text-[10px] md:text-[11px] ml-1.5">(Optional)</span>
                    </p>
                    <p className="hidden md:block text-white/85 text-[10px] leading-snug">
                      Bring your <em>maharaj</em> · cook home-style on vacation
                    </p>
                  </div>
                </div>

                {/* Arrow */}
                <FiArrowRight className="relative w-4 h-4 text-white flex-shrink-0 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          )}

          {/* ===== BOOKING SEARCH BAR (StayVista-inspired) ===== */}
          <div data-testid="hero-booking-bar" className="bg-white rounded-xl shadow-2xl p-4 md:p-6 fade-in-up fade-in-up-delay-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              {/* Villa Selector */}
              <div>
                <label className="block text-[#6B7280] text-xs font-semibold uppercase tracking-wider mb-2">Select Villa</label>
                <div className="relative">
                  <select
                    data-testid="booking-bar-villa-select"
                    value={selectedVilla}
                    onChange={e => setSelectedVilla(e.target.value)}
                    className="w-full appearance-none bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm font-medium focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20] cursor-pointer"
                  >
                    <option value="">All Villas</option>
                    {villas.map(v => (
                      <option key={v.id} value={v.id}>{withBhk(v)}</option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280] pointer-events-none" />
                </div>
              </div>

              {/* Check-in */}
              <div className="relative">
                <label className="block text-[#6B7280] text-xs font-semibold uppercase tracking-wider mb-2">Check-in</label>
                <button
                  data-testid="booking-bar-checkin"
                  onClick={() => setHomeCalendarOpen(homeCalendarOpen === 'checkin' ? null : 'checkin')}
                  className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-left text-sm font-medium focus:outline-none focus:border-[#F57C20]"
                >
                  <span className={homeCheckIn ? 'text-[#1A1A2E]' : 'text-[#9CA3AF]'}>{formatDisplay(homeCheckIn)}</span>
                </button>
                {homeCalendarOpen === 'checkin' && (
                  <div className="absolute z-50 top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl p-1 calendar-popup-mobile">
                    <div className="flex justify-between items-center px-3 pt-2">
                      <span className="text-[#6B7280] text-xs">Select Check-in</span>
                      <button onClick={() => setHomeCalendarOpen(null)} className="text-[#9CA3AF] hover:text-[#1A1A2E]"><FiX className="w-4 h-4" /></button>
                    </div>
                    <Calendar
                      mode="single"
                      selected={homeCheckIn}
                      onSelect={(date) => {
                        if (date && homeBlockedDates.has(toDateStr(date))) return;
                        setHomeCheckIn(date);
                        setHomeCalendarOpen('checkout');
                        if (homeCheckOut && date && date >= homeCheckOut) setHomeCheckOut(null);
                      }}
                      disabled={(date) => date < today || homeBlockedDates.has(toDateStr(date))}
                      modifiers={{ blocked: (date) => homeBlockedDates.has(toDateStr(date)) }}
                      modifiersClassNames={{ blocked: 'rdp-day-blocked' }}
                      className="text-[#1A1A2E]"
                    />
                  </div>
                )}
              </div>

              {/* Check-out */}
              <div className="relative">
                <label className="block text-[#6B7280] text-xs font-semibold uppercase tracking-wider mb-2">Check-out</label>
                <button
                  data-testid="booking-bar-checkout"
                  onClick={() => setHomeCalendarOpen(homeCalendarOpen === 'checkout' ? null : 'checkout')}
                  className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-left text-sm font-medium focus:outline-none focus:border-[#F57C20]"
                >
                  <span className={homeCheckOut ? 'text-[#1A1A2E]' : 'text-[#9CA3AF]'}>{formatDisplay(homeCheckOut)}</span>
                </button>
                {homeCalendarOpen === 'checkout' && (
                  <div className="absolute z-50 top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl p-1 calendar-popup-mobile">
                    <div className="flex justify-between items-center px-3 pt-2">
                      <span className="text-[#6B7280] text-xs">Select Check-out</span>
                      <button onClick={() => setHomeCalendarOpen(null)} className="text-[#9CA3AF] hover:text-[#1A1A2E]"><FiX className="w-4 h-4" /></button>
                    </div>
                    <Calendar
                      mode="single"
                      selected={homeCheckOut}
                      onSelect={(date) => {
                        if (!date) return;
                        // Same-day-turnover: allow selecting a blocked date as checkout
                        // if every stay-night (check_in .. date-1) is free.
                        if (homeCheckIn) {
                          const cur = new Date(homeCheckIn); cur.setHours(0,0,0,0);
                          const target = new Date(date); target.setHours(0,0,0,0);
                          while (cur < target) {
                            if (homeBlockedDates.has(toDateStr(cur))) return;
                            cur.setDate(cur.getDate() + 1);
                          }
                        }
                        setHomeCheckOut(date); setHomeCalendarOpen(null);
                      }}
                      disabled={(date) => {
                        if (date <= (homeCheckIn || today)) return true;
                        if (!homeCheckIn) return homeBlockedDates.has(toDateStr(date));
                        const cur = new Date(homeCheckIn); cur.setHours(0,0,0,0);
                        const target = new Date(date); target.setHours(0,0,0,0);
                        while (cur < target) {
                          if (homeBlockedDates.has(toDateStr(cur))) return true;
                          cur.setDate(cur.getDate() + 1);
                        }
                        return false;
                      }}
                      modifiers={{ blocked: (date) => {
                        if (!homeCheckIn) return homeBlockedDates.has(toDateStr(date));
                        if (date <= homeCheckIn) return false;
                        const cur = new Date(homeCheckIn); cur.setHours(0,0,0,0);
                        const target = new Date(date); target.setHours(0,0,0,0);
                        while (cur < target) {
                          if (homeBlockedDates.has(toDateStr(cur))) return true;
                          cur.setDate(cur.getDate() + 1);
                        }
                        return false;
                      } }}
                      modifiersClassNames={{ blocked: 'rdp-day-blocked' }}
                      className="text-[#1A1A2E]"
                    />
                  </div>
                )}
              </div>

              {/* Search Button */}
              <button
                data-testid="booking-bar-search"
                onClick={handleSearch}
                className="bg-[#F57C20] text-white px-6 py-3 rounded-lg text-sm font-semibold tracking-wide hover:bg-[#E06A10] transition-all flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-orange-500/20"
              >
                <FiSearch className="w-4 h-4" /> SEARCH
              </button>
            </div>
            <p className="text-[#9CA3AF] text-xs mt-3 text-center">
              Finding your ideal villa? We&apos;re here to help!
              <a href="tel:+919820088292" className="text-[#F57C20] font-semibold ml-1 hover:underline">Request Callback</a>
            </p>
          </div>
        </div>
      </section>

      {/* ===================== STATS SECTION ===================== */}
      <section data-testid="stats-section" className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: FiUsers, value: "5,000+", label: "Happy Guests" },
              { icon: FiHome, value: "4", label: "Luxury Villas" },
              { icon: FiStar, value: "35+", label: "Max Capacity" },
              { icon: FiDroplet, value: "3", label: "Private Pools" },
            ].map((stat, i) => (
              <div key={i} className="text-center py-6">
                <div className="w-12 h-12 rounded-lg bg-[#1B2432] flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-5 h-5 text-[#F57C20]" />
                </div>
                <p className="text-3xl font-bold text-[#1A1A2E] mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>{stat.value}</p>
                <p className="text-xs text-[#6B7280] font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== SERVICE ICONS (StayVista-style) ===================== */}
      <section data-testid="service-icons-section" className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-6 md:gap-4">
            {[
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/e9544835cd5789b4bf47840a124019c6a0d394dd9afadb9b8a19c4c0e8b6c8dd.png", label: "Personalised Celebrations" },
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/d36462357e8ac5fcfa9583cbca7ac7a07b8a3b2de454ab47df3e652d9894d189.png", label: "Caretaker Onsite" },
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/451462edfcf5522c6c41953ff8913fd6fce8bacd2281494cf1b3f3806ee659be.png", label: "Local Experiences" },
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/e75a9040e943e63ab5f1bf5b6ec86c3cdc6106704f6a14d348d27fe84a4bb985.png", label: "Private Pool" },
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/7eb82a50965458ab106fecfa56c6f1a3e340ee45d798979d0874779988c72ad9.png", label: "Butler Service" },
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/7a198b00df8f8de242b09635196e264305fdbc84fe93d3fe91c8a20c03421a36.png", label: "Games & Recreation" },
              { img: "https://static.prod-images.emergentagent.com/jobs/ec907cab-2737-4c0e-a014-2b1aed568ccf/images/90d72556a07bb047ae7e88b6abcaf17fd697b453ac0eca9f3beb58791484ace8.png", label: "Green Open Spaces" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center group cursor-default">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg flex items-center justify-center mb-3">
                  <img src={item.img} alt={item.label} className="w-full h-full object-contain" />
                </div>
                <p className="text-[#1A1A2E] text-xs font-semibold">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== VILLA CARDS ===================== */}
      <section data-testid="villa-highlights-section" className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10">
            <div>
              <p className="label-uppercase mb-2">Our Collection</p>
              <h2 className="text-3xl sm:text-4xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Handpicked <span className="text-[#F57C20] italic">Luxury Villas</span>
              </h2>
            </div>
            <Link to="/villas" data-testid="view-all-villas-link" className="mt-4 md:mt-0 flex items-center gap-2 text-[#F57C20] text-sm font-semibold hover:gap-3 transition-all">
              View All Villas <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="shimmer h-80 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {villas.map((villa) => (
                <Link
                  key={villa.id}
                  to={`/villas/${villa.id}`}
                  data-testid={`villa-card-${villa.id}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-xl hover:shadow-black/5 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-52 overflow-hidden">
                    {villa.is_combined && (villa.images?.length || 0) > 1 ? (
                      <VillaCardCarousel
                        images={villa.images.slice(0, 6)}
                        alt={`${villa.name} - ${villa.bedrooms}BHK luxury villa in Lonavala`}
                        villaId={villa.id}
                      />
                    ) : (
                      <img
                        src={villa.images?.[0] || HERO_IMAGE}
                        alt={`${villa.name} - ${villa.bedrooms}BHK luxury villa in Lonavala`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    {villa.is_combined && (
                      <div className="absolute top-3 left-3 z-10 bg-[#1A1A2E]/90 backdrop-blur-sm text-white text-[10px] font-bold tracking-wider px-3 py-1.5 rounded">
                        COMBO · {villa.bedrooms}BHK
                      </div>
                    )}
                    {!villa.is_combined && nightDiscountEnabled && (
                      <div className="absolute top-3 left-3 bg-[#F57C20] text-white text-[10px] font-bold tracking-wider px-3 py-1.5 rounded">
                        {nightDiscountPct}% OFF 2nd Night+
                      </div>
                    )}
                    <button className="absolute top-3 right-3 z-10 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors" onClick={e => e.preventDefault()}>
                      <FiHeart className="w-4 h-4 text-[#1A1A2E]/60" />
                    </button>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#1A1A2E] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {withBhk(villa)}
                    </h3>
                    <p className="text-[#6B7280] text-xs mb-3 flex items-center gap-1">
                      <FiMapPin className="w-3 h-3" /> Lonavala, Maharashtra
                    </p>
                    <div className="flex items-center gap-3 text-xs text-[#6B7280] mb-3">
                      <span className="flex items-center gap-1"><FiUsers className="w-3.5 h-3.5" /> {villa.base_capacity} Guests</span>
                      <span className="flex items-center gap-1"><FiHome className="w-3.5 h-3.5" /> {villa.bedrooms} Bed/{villa.bedrooms} Bath</span>
                      <span className="flex items-center gap-1"><FiDroplet className="w-3.5 h-3.5" /> Pool</span>
                    </div>
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <p className="text-[#1A1A2E]">
                          <span className="text-lg font-bold">Rs.{villa.weekend_price?.toLocaleString()}</span>
                          <span className="text-xs text-[#6B7280]"> /night</span>
                        </p>
                        <span className="w-8 h-8 rounded-full bg-[#F57C20] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <FiArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">For {villa.base_capacity} guests (addtl. guests +Rs.1,000/extra)</p>
                      <div
                        data-testid={`pay-split-badge-${villa.id}`}
                        className="mt-2.5 inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-[10px] font-semibold rounded-full px-3 py-1 whitespace-nowrap"
                      >
                        <FiCheck className="w-3 h-3" />
                        Pay 50% now, 50% a day prior to check-in
                      </div>
                    </div>
                  </div>
                </Link>
              ))}

              {/* Grid-alignment CTA — Custom Stay Requirement.
                  Placed last so the grid remains visually balanced. */}
              <Link
                to="/contact"
                data-testid="villa-card-custom-cta"
                className="group bg-gradient-to-br from-[#1A1A2E] via-[#2A2A4A] to-[#1A1A2E] text-white rounded-xl overflow-hidden border border-[#F57C20]/30 hover:border-[#F57C20] hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300 hover:-translate-y-1 flex flex-col items-center justify-center p-8 text-center min-h-[360px]"
              >
                <div className="w-16 h-16 rounded-full bg-[#F57C20]/15 border-2 border-[#F57C20]/50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <FiHome className="w-7 h-7 text-[#F57C20]" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Custom Stay <span className="italic text-[#F57C20]">Requirement?</span>
                </h3>
                <p className="text-sm text-white/70 mb-5 leading-relaxed">
                  Ohh.. your custom bedroom requirement is not available? Please connect with us!
                </p>
                <span className="inline-flex items-center gap-2 bg-[#F57C20] text-white text-sm font-semibold px-5 py-2.5 rounded-full group-hover:gap-3 transition-all">
                  Contact Us <FiArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ===================== PROMOTIONAL BANNER ===================== */}
      <section data-testid="promo-banner" className="py-14 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {(() => {
            // Dynamic column count: 2 base cards (Butler + BBQ) + optional Discount + optional Cooking.
            const extraCount = (nightDiscountEnabled ? 1 : 0) + (cookingEnabled ? 1 : 0);
            const total = 2 + extraCount;
            const colsCls = total >= 4 ? 'lg:grid-cols-4' : total === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2';
            return (
              <div className={`grid grid-cols-1 md:grid-cols-2 ${colsCls} gap-6`}>
                {/* Discount Card — hidden when discount is off */}
                {nightDiscountEnabled && (
                  <div
                    data-testid="multi-night-offer-card"
                    className="relative bg-white/5 border border-white/10 rounded-xl p-6 flex items-start gap-5 hover:border-[#F57C20]/40 transition-colors"
                  >
                    <div className="w-16 h-16 flex items-center justify-center border-2 border-[#F57C20] rounded-xl flex-shrink-0">
                      <span className="text-2xl font-bold text-[#F57C20]" style={{ fontFamily: "'Playfair Display', serif" }}>{nightDiscountPct}%</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-base font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Multi-Night Offer</p>
                      <p className="text-white/50 text-xs mb-1.5">Stay Longer, Pay Less: <span className="text-[#F57C20] font-semibold">{nightDiscountPct}% OFF</span> 2nd Night Onwards!</p>
                      <p className="text-[10px] text-amber-300 font-semibold leading-snug">
                        Available only on Request, subject to <span className="underline">Management Approval</span>.
                      </p>
                    </div>
                  </div>
                )}

                {/* Maid Service Card */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-[#F57C20]/40 transition-colors">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#F57C20]/10 rounded-xl flex-shrink-0">
                    <FiStar className="w-7 h-7 text-[#F57C20]" />
                  </div>
                  <div>
                    <p className="text-white text-base font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Extra Butler Service (Optional)</p>
                    <p className="text-white/50 text-xs">Dedicated serving maid (1 PM - 9 PM) at <span className="text-[#F57C20] font-semibold">Rs.2,000/day</span></p>
                  </div>
                </div>

                {/* BBQ Service Card */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-[#F57C20]/40 transition-colors">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#F57C20]/10 rounded-xl flex-shrink-0">
                    <FiAward className="w-7 h-7 text-[#F57C20]" />
                  </div>
                  <div>
                    <p className="text-white text-base font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>BBQ Service (Optional)</p>
                    <p className="text-white/50 text-xs">Sigdi, Metal Sticks, Charcoal & Labour from <span className="text-[#F57C20] font-semibold">Rs.3,000/meal</span></p>
                  </div>
                </div>

                {/* Dedicated Cooking Space Card (NEW) — placed alongside Butler/BBQ */}
                {cookingEnabled && (
                  <div
                    data-testid="cooking-amenity-card"
                    className="relative bg-[#F57C20]/10 border border-[#F57C20]/40 rounded-xl p-6 flex items-start gap-5 hover:border-[#F57C20] transition-colors"
                  >
                    <span className="absolute -top-2 right-4 text-[9px] font-bold tracking-widest bg-[#F57C20] text-white px-2 py-0.5 rounded-full animate-pulse">
                      ✨ NEW
                    </span>
                    <div className="w-16 h-16 flex items-center justify-center bg-[#F57C20]/20 border border-[#F57C20]/60 rounded-xl flex-shrink-0">
                      <FiCoffee className="w-7 h-7 text-[#F57C20]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-base font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Dedicated Kitchen &amp; Cooking Space
                      </p>
                      <p className="text-white/60 text-[11px] leading-snug mb-1.5">
                        Bring your own chef / <em>maharaj</em>. We provide space, utensils, cooking gas &amp; sink.
                      </p>
                      <p className="text-[10px] text-white/50">
                        <span className="text-[#F57C20] font-semibold">Rs.3,000</span> / day (3/4/5 BHK) ·{' '}
                        <span className="text-[#F57C20] font-semibold">Rs.4,500</span> / day (7/8/9 BHK) ·{' '}
                        <span className="text-[#F57C20] font-semibold">Rs.7,500</span> / day (12 BHK)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="text-center mt-6">
            <Link to="/booking" className="bg-[#F57C20] text-white px-8 py-3.5 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all inline-flex items-center gap-2 hover:shadow-lg hover:shadow-orange-500/30">
              Book & Save <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== CHOOSE A COLLECTION (StayVista-style) ===================== */}
      <section data-testid="collections-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="label-uppercase mb-2">Choose a Collection</p>
            <h2 className="text-3xl sm:text-4xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Curated <span className="text-[#F57C20] italic">Experiences</span>
            </h2>
            <p className="text-[#6B7280] text-base mt-2 max-w-lg mx-auto">
              Every celebration deserves the perfect setting. Discover our villa collections tailored for your occasion.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {COLLECTIONS.map((col, i) => (
              <Link
                key={i}
                to={col.link}
                data-testid={`collection-card-${i}`}
                className="group relative h-72 rounded-xl overflow-hidden"
              >
                <img src={col.img} alt={col.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-white text-lg font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{col.title}</h3>
                  <p className="text-white/60 text-xs mb-2">{col.desc}</p>
                  <span className="text-[#F57C20] text-xs font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <FiArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== THE ROYAL GARDEN STANDARD (Lifestyle Grid) ===================== */}
      <section data-testid="standard-section" className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="label-uppercase mb-2">The Royal Garden Standard</p>
            <h2 className="text-3xl sm:text-4xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              What Awaits <span className="text-[#F57C20] italic">You</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" style={{ gridAutoRows: '200px' }}>
            {[
              { img: "/images/lifestyle/Private Pool Villas.png", label: "Private Pool Villas" },
              { img: "/images/lifestyle/Outdoor Dining.png", label: "Outdoor Dining" },
              { img: "/images/lifestyle/Luxury Bedrooms.jpg", label: "Luxury Bedrooms" },
              { img: "/images/lifestyle/Grand Celebrations.png", label: "Grand Celebrations" },
              { img: "/images/lifestyle/NIght Pool Experience.png", label: "Night Pool Experience" },
              { img: "/images/lifestyle/Green Festivities.png", label: "Garden Festivities" },
            ].map((item, i) => (
              <div key={i} className={`relative overflow-hidden rounded-xl group ${i === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}>
                <img src={item.img} alt={item.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-sm font-semibold">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GUEST TESTIMONIALS (Polaroid Wall — real WhatsApp screenshots) ===================== */}
      <GuestReviewWall />

      {/* ===================== TRUST SIGNALS / FEATURED STRIP ===================== */}
      <section data-testid="trust-section" className="py-14 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-white/40 text-xs font-semibold tracking-[0.2em] uppercase mb-8">Your Trusted Getaway Partner</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: FiShield, title: "Verified Property", desc: "All villas inspected and verified" },
              { icon: FiAward, title: "Rated 4.8/5", desc: "5000+ happy guest stays" },
              { icon: FiWifi, title: "100% Digital", desc: "Online booking, e-sign, payment" },
              { icon: FiCalendar, title: "Instant Confirmation", desc: "Real-time availability updates" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#F57C20]/10 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-[#F57C20]" />
                </div>
                <p className="text-white text-sm font-semibold mb-1">{item.title}</p>
                <p className="text-white/40 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== PRICING TABLE ===================== */}
      <section data-testid="pricing-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="label-uppercase mb-2">Transparent Pricing</p>
            <h2 className="text-3xl sm:text-4xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Villa <span className="text-[#F57C20] italic">Rates & Capacity</span>
            </h2>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm" data-testid="pricing-table">
              <thead>
                <tr className="bg-[#1B2432] text-white">
                  <th className="text-left py-4 px-5 font-semibold text-xs tracking-wider uppercase">Villa</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Bedrooms</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Washrooms</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Base Guests</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Max Guests</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Weekday</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Weekend</th>
                  <th className="text-center py-4 px-5 font-semibold text-xs tracking-wider uppercase">Deposit</th>
                </tr>
              </thead>
              <tbody>
                {!loading && villas.map((v, idx) => (
                  <tr key={v.id} className={`border-b border-gray-100 hover:bg-[#F57C20]/5 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-[#F7F8FA]'}`}>
                    <td className="py-4 px-5">
                      <Link to={`/villas/${v.id}`} className="text-[#1A1A2E] hover:text-[#F57C20] font-semibold transition-colors">{withBhk(v, v.short_name || v.name)}</Link>
                    </td>
                    <td className="py-4 px-5 text-center text-[#6B7280]">{v.bedrooms}</td>
                    <td className="py-4 px-5 text-center text-[#6B7280]">{v.bedrooms}</td>
                    <td className="py-4 px-5 text-center text-[#6B7280]">{v.base_capacity} incl kids</td>
                    <td className="py-4 px-5 text-center text-[#6B7280]">{v.capacity}</td>
                    <td className="py-4 px-5 text-center text-[#1A1A2E] font-semibold">Rs.{v.base_price?.toLocaleString()}</td>
                    <td className="py-4 px-5 text-center text-[#1A1A2E] font-semibold">Rs.{v.weekend_price?.toLocaleString()}</td>
                    <td className="py-4 px-5 text-center text-[#6B7280]">Rs.{v.security_deposit?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#9CA3AF] text-xs mt-3 text-center">
            * Weekend rates apply Fri-Sun. Extra guests: Rs.1,000/person/night above base capacity. All prices in INR.
          </p>
        </div>
      </section>

      {/* ===================== ABOUT SECTION ===================== */}
      <section data-testid="about-section" className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="label-uppercase mb-3">About Royal Garden Villas</p>
              <h2 className="text-3xl sm:text-4xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Lonavala&apos;s Premier <span className="text-[#F57C20] italic">Private Villa</span> Destination
              </h2>
              <div className="space-y-4 text-[#6B7280] leading-relaxed text-sm">
                <p>
                  Nestled amidst the breathtaking Western Ghats, Royal Garden Villas offers an unparalleled luxury experience.
                  Our collection of 3BHK, 4BHK, 5BHK, and the exclusive 12BHK Celebration Villa provides the perfect setting for every occasion.
                </p>
                <p>
                  Whether you&apos;re planning a birthday, family reunion, corporate offsite, or romantic getaway, our villas feature
                  private swimming pools, manicured gardens, fully-equipped kitchens, AC, complimentary WiFi, and dedicated parking.
                </p>
                <p>
                  Located just 2 hours from Mumbai and 1.5 hours from Pune along the Mumbai-Pune Expressway,
                  our properties are the perfect weekend escape.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden rounded-xl">
                <img src="https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/kvat1x2q_2.jpg" alt="Royal Garden Villas aerial view Lonavala" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-xl mt-8">
                <img src="/images/5bhk/1.jpg" alt="5BHK villa garden Lonavala" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== LOCATION & NEARBY ===================== */}
      <section data-testid="location-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-14">
            <div>
              <p className="label-uppercase mb-3">Location</p>
              <h2 className="text-3xl sm:text-4xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Nestled in <span className="text-[#F57C20] italic">Lonavala</span>
              </h2>
              <p className="text-[#6B7280] leading-relaxed mb-6 text-sm">
                One of India&apos;s most popular hill stations in the Western Ghats. Cool climate, stunning valley views, waterfalls, and lush greenery year-round.
              </p>
              <ul className="space-y-3">
                {[
                  "83 km from Mumbai (2 hours via Expressway)",
                  "64 km from Pune (1.5 hours drive)",
                  "Nearest railway station: Lonavala (5 km)",
                  "Private gated community, 24/7 security",
                  "Altitude: 625 meters above sea level"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <FiMapPin className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />
                    <span className="text-[#6B7280] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col justify-center">
              <p className="label-uppercase mb-3">Explore Around</p>
              <h3 className="text-3xl sm:text-4xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Nearby <span className="text-[#F57C20] italic">Attractions</span>
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                Family-friendly attractions, waterfalls and viewpoints — all a short drive from your villa.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {NEARBY.map((place, i) => (
              <div
                key={i}
                data-testid={`nearby-card-${i}`}
                className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-[#F57C20]/40 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                {/* Image */}
                <div className="relative h-44 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden flex items-center justify-center">
                  <img
                    src={place.img}
                    alt={`${place.name} — attraction near Royal Garden Villas`}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="absolute top-2.5 right-2.5 bg-white/95 backdrop-blur-sm text-[#F57C20] text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                    {place.dist}
                  </span>
                </div>
                {/* Content */}
                <div className="p-4 flex-1">
                  <p className="text-[#1A1A2E] text-sm font-bold leading-snug mb-1">{place.name}</p>
                  <p className="text-[#6B7280] text-xs leading-relaxed">{place.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHY CHOOSE US (Dark) ===================== */}
      <section data-testid="why-choose-section" className="py-20 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-2">Why Royal Garden</p>
            <h2 className="text-3xl sm:text-4xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why Guests <span className="text-[#F57C20] italic">Choose Us</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Family-First Approach", desc: "Kid-friendly private pool villas with gardens, indoor games, and safe spaces. We cater to families of all sizes.", icon: FiUsers },
              ...(nightDiscountEnabled ? [{ title: "Stay Longer, Pay Less", desc: `1st night at actual rate. From the 2nd night onwards, every additional night gets a flat ${nightDiscountPct}% off.`, icon: FiPercent }] : []),
              { title: "100% Digital Process", desc: "Online booking, e-signed indemnity, Razorpay payment. Deposit refunded within 1 working day.", icon: FiShield },
              { title: "Extra Butler Service (Optional)", desc: "Opt for a dedicated maid (1 PM - 9 PM) to serve lunch, high tea & dinner at Rs.2,000/day. Serving only, not cooking/supply.", icon: FiStar },
              { title: "BBQ & Bonfire Services (Optional)", desc: "BBQ with Sigdi, Metal Sticks, Charcoal & Labour at Rs.3,000/meal. Extra labour +Rs.1,000/person. Bonfire at Rs.2,000/session.", icon: FiAward },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-8 hover:border-[#F57C20]/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-[#F57C20]/10 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#F57C20]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== IDEAL FOR ===================== */}
      <section data-testid="ideal-for-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="label-uppercase mb-2">Perfect For Every Occasion</p>
            <h2 className="text-3xl sm:text-4xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ideal <span className="text-[#F57C20] italic">For</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Family Holidays", to: '/family-weekend-getaway-lonavala' },
              { label: "Kids' Birthday Parties", to: '/intimate-celebrations-lonavala' },
              { label: "Family Reunions", to: '/family-weekend-getaway-lonavala' },
              { label: "Baby Showers", to: '/intimate-celebrations-lonavala' },
              { label: "Anniversary Celebrations", to: '/intimate-celebrations-lonavala' },
              { label: "Kitty Parties", to: null },
              { label: "Weekend Getaways", to: '/family-weekend-getaway-lonavala' },
              { label: "Engagement Parties", to: '/intimate-celebrations-lonavala' },
              { label: "New Year Parties", to: '/intimate-celebrations-lonavala' },
              { label: "Corporate Offsites", to: '/corporate' },
              { label: "Film & TV Shoots", to: '/shooting' },
              { label: "Weddings", to: '/intimate-celebrations-lonavala' },
            ].map((item, i) => {
              const inner = (
                <>
                  <FiCheck className="w-4 h-4 text-[#F57C20] mx-auto mb-2" />
                  <p className="text-[#1A1A2E]/70 text-sm font-medium">{item.label}</p>
                </>
              );
              const cls = "block text-center py-4 px-3 bg-[#F7F8FA] rounded-lg border border-gray-100 hover:border-[#F57C20]/30 hover:bg-white transition-colors";
              return item.to ? (
                <Link key={i} to={item.to} className={cls} data-testid={`ideal-for-${i}`}>
                  {inner}
                </Link>
              ) : (
                <div key={i} className={cls.replace('block ', '')}>{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section data-testid="faq-section" className="py-20 bg-[#F7F8FA]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="label-uppercase mb-2">Have Questions?</p>
            <h2 className="text-3xl sm:text-4xl text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Frequently Asked <span className="text-[#F57C20] italic">Questions</span>
            </h2>
          </div>
          <FAQSection
            title=""
            testid="home-faq"
            compact
            items={FAQ_ITEMS.map(({ q, a }) => ({
              q: String(q).replace(/\{PCT\}/g, nightDiscountPct),
              a: stripDiscount(String(a).replace(/\{PCT\}/g, nightDiscountPct).replace(/15%/g, `${nightDiscountPct}%`), nightDiscountEnabled),
            })).filter(({ q }) => nightDiscountEnabled || !/discount|% off/i.test(q))}
          />
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section data-testid="cta-section" className="py-20 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-2">Ready to Experience Luxury?</p>
          <h2 className="text-3xl sm:text-4xl text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Book Your <span className="text-[#F57C20] italic">Dream Villa</span> Today
          </h2>
          {nightDiscountEnabled && <p className="text-[#F57C20] text-base font-semibold mb-2">Stay Longer, Pay Less: {nightDiscountPct}% OFF 2nd Night Onwards!</p>}
          <p className="text-white/50 max-w-xl mx-auto mb-8 text-sm">
            Whether it&apos;s a birthday, family reunion, corporate retreat, or a romantic escape
            &mdash; we have the perfect property for you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/villas" data-testid="cta-explore-btn" className="bg-[#F57C20] text-white px-10 py-4 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all inline-flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-orange-500/30">
              Explore Villas <FiArrowRight className="w-4 h-4" />
            </Link>
            <a href="https://wa.me/919820088292" target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn" className="border-2 border-white/30 text-white px-10 py-4 text-sm font-semibold rounded-lg hover:border-white hover:bg-white/10 transition-all text-center">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
